import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Crown, Sparkles, X, Music, BookOpen, Mic, Trophy, Heart } from "lucide-react";

const tierFeatures = {
  seedling: {
    name: "Seedling",
    price: "FREE",
    angel: "FREE",
    color: "from-green-500 to-emerald-500",
    features: ["Soul Circle Community", "Daily Affirmations & Vibe Check", "Journal (10/mo)", "Sound Sanctuary starter & more..."]
  },
  rooted: {
    name: "Rooted", 
    price: "$5.55",
    angel: "555",
    color: "from-violet-500 to-purple-500",
    popular: true,
    features: ["Full Readables Library + Audio", "Wellness & Lifestyle (AI meal plans)", "The Game — Courageous Conversations", "Personalized Meditations", "Rooted Resources & more..."]
  },
  lifetime: {
    name: "Forever Rooted",
    price: "$111.11", 
    angel: "1111",
    color: "from-amber-500 to-orange-500",
    isLifetime: true,
    features: ["Everything in Rooted Forever", "All Future Features", "Founding Member Badge", "Exclusive Events & more..."]
  },
  // Legacy mappings — keep older tier IDs pointing at the current Rooted copy
  // so old paywall calls keep working without a deprecation flag day.
  essence: {
    name: "Rooted",
    price: "$5.55",
    angel: "555",
    color: "from-violet-500 to-purple-500",
    popular: true,
    features: ["Full Readables Library + Audio", "Wellness & Lifestyle (AI meal plans)", "The Game — Courageous Conversations", "Soul Circle & more..."]
  },
  bloom: {
    name: "Rooted", 
    price: "$5.55",
    angel: "555",
    color: "from-violet-500 to-purple-500",
    popular: true,
    features: ["Full Readables Library + Audio", "Wellness & Lifestyle (AI meal plans)", "The Game — Courageous Conversations", "Soul Circle & more..."]
  },
  transcend: {
    name: "Forever Rooted",
    price: "$111.11", 
    angel: "1111",
    color: "from-amber-500 to-orange-500",
    isLifetime: true,
    features: ["Everything in Rooted Forever", "All Future Features", "Founding Member Badge", "Exclusive Events & more..."]
  }
};

const featureIcons = {
  "Sound Sanctuary": Music,
  "Journal": BookOpen,
  "Custom Affirmations": Mic,
  "Leaderboard": Trophy,
  "Guided Meditations": Heart,
  "default": Sparkles
};

const UpgradeModal = ({ isOpen, onClose, feature = "this feature", recommendedTier = "bloom" }) => {
  if (!isOpen) return null;

  const tier = tierFeatures[recommendedTier] || tierFeatures.bloom;
  const FeatureIcon = featureIcons[feature] || featureIcons.default;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-background rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-border overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-amber-500/5 to-emerald-500/5"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-accent/20 to-transparent rounded-full blur-2xl"></div>
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/50 transition-colors z-10"
          data-testid="upgrade-modal-close"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>

        <div className="relative z-10 text-center">
          {/* Icon */}
          <div className="mb-5 relative">
            <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${tier.color} flex items-center justify-center shadow-lg`}>
              <Crown className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-background border-2 border-accent flex items-center justify-center">
              <FeatureIcon className="h-5 w-5 text-accent" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl md:text-2xl font-bold text-primary mb-2">
            Unlock {feature}
          </h2>
          
          <p className="text-muted-foreground text-sm mb-5">
            This premium feature is available with the {tier.name} tier and above.
          </p>

          {/* Recommended Tier Card */}
          <div className={`rounded-2xl p-4 mb-5 bg-gradient-to-br ${tier.color} text-white relative overflow-hidden`}>
            {tier.popular && (
              <span className="absolute top-2 right-2 text-[10px] bg-white/20 px-2 py-0.5 rounded-full">
                Most Popular
              </span>
            )}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs opacity-80">Angel {tier.angel}</p>
                <p className="text-lg font-bold">{tier.name}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{tier.price}</p>
                <p className="text-xs opacity-80">/month</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs text-left">
              {tier.features.slice(0, 4).map((f, i) => (
                <div key={i} className="flex items-center gap-1 opacity-90">
                  <Sparkles className="h-3 w-3" />
                  <span className="truncate">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Link to="/subscription" onClick={onClose}>
              <Button 
                className={`w-full bg-gradient-to-r ${tier.color} hover:opacity-90 text-white rounded-full py-5`}
                data-testid="upgrade-modal-cta"
              >
                <Crown className="h-4 w-4 mr-2" />
                Choose Your Path
              </Button>
            </Link>
            <button 
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Maybe later
            </button>
          </div>

          {/* Angel number meaning */}
          <p className="text-xs text-muted-foreground mt-4 italic">
            {tier.angel === "FREE" && "Start your spiritual journey for free"}
            {tier.angel === "555" && "555 represents transformation & positive change"}
            {tier.angel === "1111" && "1111 symbolizes new beginnings & manifestation"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
